/**
 * BXAY Single Tab runtime — grid + Mini-PDP + bottom bar wiring.
 *
 * Phase 1: every product renders as a product card. Click opens the Mini-PDP
 * modal where the shopper picks every variant option, then "Add to Bundle"
 * appends a line. The sticky bottom bar reflects state via Spectrum.z99nr29sCart.
 *
 * Phase 2 will add variant explosion: when a product has
 * `basisAttribute` set, the grid renders N variant cards (one per basis
 * value), and the Mini-PDP opens with that axis pre-selected.
 */
;(() => {
  const FEATURE_SLUG = 'bxay_single_tab'

  // Lucide "trash-2" — inline SVG (no Unicode glyph icons, per snippet rules).
  const TRASH_ICON =
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/></svg>'

  /** HTML-escape for innerHTML interpolations. Mirrors `sct9fpdr`'s helper —
   *  size labels etc. routinely contain `"` characters that break attribute
   *  context if injected raw. */
  function escapeHtml(s) {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }

  /**
   * Decompose a product into renderable cards.
   *
   * Phase 1 behaviour (no basis) → one product card per product.
   * Phase 2 (basis set)          → one card per value of the basis axis.
   *
   * When the basis covers the product's ONLY axis, the resulting variant
   * card is "fully resolved" — click adds directly (no Mini-PDP). When the
   * product has other axes (e.g. basis=Color but product also has Size),
   * each variant card carries `lockedOptions` that the Mini-PDP locks
   * down so the shopper only picks the remaining axes (R15).
   */
  function explodeProduct(product) {
    if (!product.basisAttribute) {
      return [
        { kind: 'product', product, lockedOptions: {}, basisValue: null, basisPosition: null },
      ]
    }
    const basis = product.options.find((o) => o.name === product.basisAttribute)
    if (!basis) {
      // Misconfigured — basis attribute name doesn't match any Shopify option.
      // Degrade gracefully to product-card mode.
      return [
        { kind: 'product', product, lockedOptions: {}, basisValue: null, basisPosition: null },
      ]
    }
    return basis.values.map((value) => ({
      kind: 'variant',
      product,
      lockedOptions: { [basis.position]: value },
      basisValue: value,
      basisPosition: basis.position,
    }))
  }

  /** Resolve the variant matching the locked selection, or null if no match. */
  function findVariantForLocked(product, lockedOptions, fillFirst) {
    for (const v of product.variants) {
      let match = true
      for (let pos = 1; pos <= 3; pos++) {
        const want = lockedOptions[pos]
        if (want != null && v[`option${pos}`] !== want) {
          match = false
          break
        }
      }
      if (match && (!fillFirst || v.available)) return v
    }
    return null
  }

  /** True when the basis covers every option axis the product has — click
   *  on the card resolves directly to a single variant without a modal. */
  function isFullyResolvedByBasis(product, basis) {
    if (!basis) return false
    const axes = product.options.length
    return axes === 1 && product.options[0].name === product.basisAttribute
  }

  /** True when at least one variant matches the locked selection AND is
   *  available — used to dim/mark OOS cards. */
  function lockedSelectionAvailable(product, lockedOptions) {
    for (const v of product.variants) {
      let match = true
      for (let pos = 1; pos <= 3; pos++) {
        const want = lockedOptions[pos]
        if (want != null && v[`option${pos}`] !== want) {
          match = false
          break
        }
      }
      if (match && v.available) return true
    }
    return false
  }

  function noopTrack() {}
  function safeTrack(track) {
    if (typeof track !== 'function') return noopTrack
    return (eventName, payload) => {
      try {
        track(eventName, payload)
      } catch (err) {
        console.warn(`[${FEATURE_SLUG}] analytics dispatch failed`, err)
      }
    }
  }

  /** Store currency captured from the container's data-currency attribute
   *  (SSR'd from cart.currency.iso_code). Module-level: one store per page. */
  let storeCurrency = null

  /** Format a Shopify minor-unit amount in the store currency. Falls back to
   *  a bare grouped number when the currency is unknown — a missing symbol is
   *  visible and debuggable; a wrong hardcoded one is not. */
  function formatMoney(cents) {
    const n = Number(cents)
    if (!Number.isFinite(n)) return ''
    const value = n / 100
    if (storeCurrency) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: storeCurrency,
          maximumFractionDigits: value % 1 === 0 ? 0 : 2,
        }).format(value)
      } catch {
        // Unrecognized currency code — fall through to the bare number.
      }
    }
    return value.toLocaleString()
  }

  /** Format a ms duration as HH:MM:SS (or MM:SS when <1h).
   *  Used by the urgency-banner countdown — must stay tabular-numeric. */
  function formatDuration(ms) {
    const totalSec = Math.max(0, Math.floor(ms / 1000))
    const h = Math.floor(totalSec / 3600)
    const m = Math.floor((totalSec % 3600) / 60)
    const s = totalSec % 60
    const pad = (n) => String(n).padStart(2, '0')
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
  }

  class SaiZ99nr29sElement extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return
      this._mounted = true

      // Fixed curated-bundle mode: the whole manifest is SSR-rendered; this
      // runtime only wires the single add-whole-bundle CTA. The grid /
      // Mini-PDP / sticky-bar picking flow below is Build Your Own only.
      storeCurrency = this.dataset.currency || storeCurrency

      const fixedTag = this.querySelector('script[data-spectrum-fixed-pool]')
      if (fixedTag) {
        this._initFixed(fixedTag)
        return
      }

      const tag = this.querySelector('script[data-spectrum-snippet-pool]')
      if (!tag) return
      let pool
      try {
        pool = JSON.parse(tag.textContent || '{}')
      } catch {
        return
      }
      if (!pool.config || !pool.products) return

      this._pool = pool
      this._config = pool.config
      this._products = pool.products
      this._props = pool.props || {}

      // Variant explosion is driven by the snippet-level `basisOptionIndex`
      // prop (option1/2/3), not a per-product metafield. Resolve the option
      // position to each product's option NAME up front so the name-based
      // explosion logic (explodeProduct etc.) applies uniformly. 'none' (or an
      // out-of-range position) leaves basisAttribute null → one card per product.
      const basisPos = { option1: 1, option2: 2, option3: 3 }[this._config.basisOptionIndex] ?? 0
      for (const product of this._products) {
        const opt = basisPos >= 1 ? (product.options || [])[basisPos - 1] : null
        product.basisAttribute = opt ? opt.name : null
      }

      this._track = noopTrack
      this._minipdpProductIdx = -1
      this._minipdpSelection = {}

      this._grid = this.querySelector('[data-spectrum-bxay-grid]')
      this._minipdp = this.querySelector('[data-spectrum-bxay-minipdp]')
      this._bar = this.querySelector('[data-spectrum-bxay-bar]')

      const snippetApi = window.__spectrumAi?.snippet
      if (snippetApi && typeof snippetApi.bind === 'function') {
        const handles = snippetApi.bind(this, () => {})
        if (handles?.track) this._track = safeTrack(handles.track)
      }

      const cart = window.Spectrum?.z99nr29sCart
      if (!cart) {
        console.warn(`[${FEATURE_SLUG}] z99nr29sCart engine not loaded`)
        return
      }
      cart.init(this._config)

      this._unsubscribeCart = cart.subscribe((state) => this._paint(state))
      this._renderGrid()
      this._bindEvents()
      this._startUrgencyTimer()
      this._track(`${FEATURE_SLUG}:page_view`, {
        config_handle: this._config.handle,
        collection_handle: this._config.collectionHandle,
        product_count: this._products.length,
      })
    }

    // ── Fixed curated-bundle mode ──
    // The manifest is server-rendered (visible without JS). This wires the
    // single Buy CTA to one add-whole-bundle cart action and, when a portal
    // Bundles entry is bound, reconciles the displayed savings against the
    // server-authoritative cost — failing loud on mismatch rather than
    // redirecting to a checkout whose price contradicts the shown savings.
    _initFixed(tag) {
      let pool
      try {
        pool = JSON.parse(tag.textContent || '{}')
      } catch {
        return
      }
      const config = pool.config || {}
      const items = Array.isArray(pool.items) ? pool.items : []
      const displayedSavings = Number(pool.displayedSavings) || 0
      const hasOos = pool.hasOos === true

      this._track = noopTrack
      const snippetApi = window.__spectrumAi?.snippet
      if (snippetApi && typeof snippetApi.bind === 'function') {
        const handles = snippetApi.bind(this, () => {})
        if (handles?.track) this._track = safeTrack(handles.track)
      }

      const cta = this.querySelector('[data-action="fixed-checkout"]')
      const warn = this.querySelector('[data-slot="fixed-warn"]')
      const cart = window.Spectrum?.z99nr29sCart

      // No pre-hydration here: addManifest's own response carries the
      // authoritative pricing (serverSavings) the reconcile reads, so an
      // up-front bundle-cart fetch would be an unused network round-trip.

      if (!cta) return
      if (hasOos) {
        cta.disabled = true
        return
      }
      if (!cart || typeof cart.addManifest !== 'function') {
        // Fail loud: no cart engine, no way to add the bundle.
        console.warn(`[${FEATURE_SLUG}] z99nr29sCart engine not loaded — fixed CTA inert`)
        cta.disabled = true
        return
      }

      cta.addEventListener('click', async () => {
        if (cta.disabled || items.length === 0) return
        const prevLabel = cta.textContent
        cta.disabled = true
        cta.textContent = 'Adding…'

        // Single cart action: the whole manifest in one call (server bundle
        // cart when bound, native /cart/add.js { items: [] } when not).
        const result = await cart.addManifest(items, config.bundleSlug || null)

        if (!result || !result.ok) {
          cta.disabled = false
          cta.textContent = prevLabel
          if (warn) {
            warn.hidden = false
            warn.textContent = 'Something went wrong adding this bundle. Please try again.'
          }
          return
        }

        // Track only once the add actually succeeded — a click that fails
        // (OOS, network, engine missing) must not inflate the funnel.
        this._track(`${FEATURE_SLUG}:add_to_cart`, {
          config_handle: config.handle,
          item_count: items.length,
          bundle_mode: 'fixed',
        })

        // Reconcile (bound mode only). serverSavings is null when unbound —
        // then the displayed compare-at savings equals the cart, no drift.
        if (config.bundleSlug && result.serverSavings != null) {
          const drift = Math.abs(Number(result.serverSavings) - displayedSavings)
          if (drift > 1) {
            this._track(`${FEATURE_SLUG}:fixed_reconcile_mismatch`, {
              config_handle: config.handle,
              displayed_savings: displayedSavings,
              server_savings: Number(result.serverSavings),
            })
            cta.disabled = false
            cta.textContent = prevLabel
            if (warn) {
              warn.hidden = false
              warn.textContent =
                "This bundle's price doesn't match the savings shown. Please contact us before checking out."
            }
            return
          }
        }

        if (result.checkoutUrl) window.location.href = result.checkoutUrl
      })
    }

    disconnectedCallback() {
      if (this._unsubscribeCart) this._unsubscribeCart()
      if (this._urgencyInterval) {
        clearInterval(this._urgencyInterval)
        this._urgencyInterval = null
      }
      if (this._onKeyDown) {
        document.removeEventListener('keydown', this._onKeyDown)
        this._onKeyDown = null
      }
    }

    // ── Grid render ──

    _renderGrid() {
      if (!this._grid) return
      // Phase 2: build the flat card list by exploding any product with a
      // basis attribute. `_cards` is the source of truth for click handling.
      // `oosDisplay` modes:
      //   • `show_disabled` (default) — render dimmed + disabled, can't click.
      //   • `show_with_badge` — render at full opacity with a "Sold out"
      //     badge overlay, still disabled (Mini-PDP would have no buyable
      //     variant — block at the card boundary).
      //   • `hide` — drop the card from `_cards` entirely. Drops the index
      //     before paint so click handlers can't fire on a hidden card.
      const oosMode = this._config.oosDisplay ?? 'show_disabled'
      this._cards = []
      for (let i = 0; i < this._products.length; i++) {
        const cards = explodeProduct(this._products[i])
        for (const c of cards) {
          const oos = this._isCardOos(c)
          if (oos && oosMode === 'hide') continue
          this._cards.push({ ...c, productIdx: i })
        }
      }

      const html = []
      for (let cardIdx = 0; cardIdx < this._cards.length; cardIdx++) {
        const c = this._cards[cardIdx]
        const p = c.product
        const isVariant = c.kind === 'variant'

        // Resolve the card's image + price + availability based on whether
        // it's a product card or a variant card.
        const variantHint = isVariant ? findVariantForLocked(p, c.lockedOptions, false) : null
        const image = (isVariant && variantHint?.image) || p.image
        const price = isVariant && variantHint ? variantHint.price : p.price
        const compareAtPrice =
          isVariant && variantHint ? variantHint.compareAtPrice : p.compareAtPrice
        const oos = this._isCardOos(c)
        const showCompare =
          this._props.showCompareAtPrice && compareAtPrice && compareAtPrice > price

        const ariaLabel = isVariant ? `${p.title} — ${c.basisValue ?? ''}` : p.title
        const subtitle = isVariant
          ? `<span class="sai-z99nr29s__card-subtitle">${escapeHtml(c.basisValue ?? '')}</span>`
          : ''
        const compareAtHtml = showCompare
          ? `<span class="sai-z99nr29s__card-compare-at">${formatMoney(compareAtPrice)}</span>`
          : ''
        const priceHtml = this._props.showPricePerCard
          ? `<div class="sai-z99nr29s__card-prices"><span class="sai-z99nr29s__card-price">${formatMoney(price)}</span>${compareAtHtml}</div>`
          : ''
        const oosBadge =
          oos && oosMode === 'show_with_badge'
            ? '<span class="sai-z99nr29s__card-oos-badge">Sold out</span>'
            : ''
        // The card is a container; the image opens the Mini-PDP and the action
        // slot holds an ADD button (default) that `_paintCardStates` swaps for a
        // quantity stepper once the product's variant is in the bundle.
        const addBtn = oos
          ? ''
          : `<button type="button" class="sai-z99nr29s__card-add" data-action="open-minipdp" data-card-idx="${cardIdx}">ADD</button>`
        html.push(`
          <div class="sai-z99nr29s__card${oos ? ' is-oos' : ''}"
               data-card-idx="${cardIdx}"
               data-product-handle="${escapeHtml(p.handle)}">
            <button type="button"
                    class="sai-z99nr29s__card-imgbtn"
                    data-action="open-minipdp"
                    data-card-idx="${cardIdx}"
                    ${oos ? 'disabled' : ''}
                    aria-label="${escapeHtml(ariaLabel)}">
              <span class="sai-z99nr29s__card-img" style="background-image: url('${escapeHtml(image ?? '')}')" aria-hidden="true"></span>
              ${oosBadge}
            </button>
            <div class="sai-z99nr29s__card-body">
              <span class="sai-z99nr29s__card-title">${escapeHtml(p.title)}</span>
              ${subtitle}
              ${priceHtml}
              <div class="sai-z99nr29s__card-action" data-slot="action" data-card-idx="${cardIdx}">${addBtn}</div>
            </div>
          </div>
        `)
      }
      this._grid.innerHTML = html.join('')
    }

    /** Shared OOS predicate — encapsulates the variant vs product split so
     *  filtering (oosDisplay === 'hide') and render-time `is-oos` use the
     *  exact same definition. */
    _isCardOos(c) {
      if (c.kind === 'variant') return !lockedSelectionAvailable(c.product, c.lockedOptions)
      return !c.product.available
    }

    // ── Mini-PDP ──

    /**
     * Open the Mini-PDP for a card. When called from a variant card, the
     * basis axis is pre-selected and rendered as locked (greyed swatch +
     * no click). When the card is "fully resolved by basis" (single-axis
     * product), we skip the Mini-PDP and add the variant directly.
     */
    _openMinipdpForCard(cardIdx) {
      const card = this._cards?.[cardIdx]
      if (!card) return
      const product = card.product

      // Single-axis product with basis = that axis → R14, no modal.
      if (card.kind === 'variant') {
        const basis = product.options.find((o) => o.name === product.basisAttribute)
        if (basis && isFullyResolvedByBasis(product, basis)) {
          const variant = findVariantForLocked(product, card.lockedOptions, true)
          if (variant?.available) {
            this._addVariantToBundle(product, variant)
          }
          return
        }
      }

      this._minipdpProductIdx = card.productIdx
      this._minipdpLockedOptions = card.lockedOptions
      this._minipdpSelection = {}
      // Locked axes start selected at their locked value; the rest default to
      // the first available value (which the shopper can change).
      for (const opt of product.options) {
        const locked = card.lockedOptions[opt.position]
        this._minipdpSelection[opt.position] = locked ?? opt.values[0]
      }
      this._renderMinipdpBody(product)
      this._minipdp.dataset.open = 'true'
      this._minipdp.setAttribute('aria-hidden', 'false')
      this._track(`${FEATURE_SLUG}:product_click`, {
        product_handle: product.handle,
        card_kind: card.kind,
        basis_value: card.basisValue,
      })
    }

    // Back-compat shim — older call sites referenced `_openMinipdp(idx)` with
    // a product index. New callers go through `_openMinipdpForCard(cardIdx)`.
    _openMinipdp(productIdx) {
      // Find the first card belonging to this product (its product card or
      // its first variant card).
      const cardIdx = (this._cards ?? []).findIndex((c) => c.productIdx === productIdx)
      if (cardIdx === -1) return
      this._openMinipdpForCard(cardIdx)
    }

    _closeMinipdp() {
      this._minipdp.dataset.open = 'false'
      this._minipdp.setAttribute('aria-hidden', 'true')
      this._minipdpProductIdx = -1
    }

    _renderMinipdpBody(product) {
      const titleEl = this._minipdp.querySelector('[data-slot="title"]')
      const bodyEl = this._minipdp.querySelector('[data-slot="body"]')
      if (titleEl) titleEl.textContent = product.title
      if (!bodyEl) return

      const locked = this._minipdpLockedOptions ?? {}
      const resolvedVariant = this._resolveMinipdpVariant(product)

      // Carousel: resolved-variant image first, then the product's gallery
      // (deduped), falling back to the featured image. One slide = one tile.
      const imgs = []
      const pushImg = (u) => {
        if (u && !imgs.includes(u)) imgs.push(u)
      }
      pushImg(resolvedVariant?.image)
      if (Array.isArray(product.images)) for (const u of product.images) pushImg(u)
      pushImg(product.image)
      const html = [
        `<div class="sai-z99nr29s__minipdp-carousel">${imgs
          .map(
            (u) =>
              `<span class="sai-z99nr29s__minipdp-slide" style="background-image: url('${escapeHtml(u)}')"></span>`,
          )
          .join('')}</div>`,
      ]

      for (const opt of product.options) {
        const lockedValue = locked[opt.position]
        const isLocked = lockedValue != null
        const selected = isLocked ? lockedValue : this._minipdpSelection[opt.position]
        html.push('<div class="sai-z99nr29s__minipdp-optgroup">')
        html.push(
          `<div class="sai-z99nr29s__minipdp-section-label">${escapeHtml(opt.name)}: <span class="sai-z99nr29s__minipdp-selected">${escapeHtml(selected ?? '')}</span></div>`,
        )
        html.push('<div class="sai-z99nr29s__minipdp-row">')
        for (const value of opt.values) {
          const isSel = selected === value
          const available = isLocked
            ? value === lockedValue
            : this._isOptionValueAvailableWithLock(product, opt.position, value, locked)
          const cls = `sai-z99nr29s__minipdp-pill${isSel ? ' is-selected' : ''}${available ? '' : ' is-oos'}`
          // Two-tone: split the option value verbatim, e.g. "S (2-5 UK)" →
          // main "S" + sub "(2-5 UK)". No paren → single label.
          const m = String(value).match(/^(.*?)\s*(\(.*\))\s*$/)
          const main = escapeHtml(m ? m[1] : value)
          const sub = m ? `<span class="sai-z99nr29s__minipdp-pill-sub">${escapeHtml(m[2])}</span>` : ''
          const attrs = isLocked
            ? 'aria-disabled="true" disabled'
            : available
              ? `data-action="pick-option" data-position="${opt.position}" data-value="${escapeHtml(value)}"`
              : 'disabled'
          html.push(
            `<button type="button" class="${cls}" ${attrs}><span class="sai-z99nr29s__minipdp-pill-main">${main}</span>${sub}</button>`,
          )
        }
        html.push('</div></div>')
      }

      const price = resolvedVariant?.price ?? product.price
      html.push(`<div class="sai-z99nr29s__minipdp-price">${formatMoney(price)}</div>`)
      bodyEl.innerHTML = html.join('')

      this._paintMinipdpCta(product, resolvedVariant)
    }

    /** Footer of the Mini-PDP: ADD TO CART, or a quantity stepper when this
     *  exact variant is already in the bundle (mirrors the card stepper). */
    _paintMinipdpCta(product, variant) {
      const slot = this._minipdp?.querySelector('[data-slot="minipdp-cta"]')
      if (!slot) return
      const cart = window.Spectrum?.z99nr29sCart
      const vid = variant?.id
      const inCart = cart && vid != null ? cart.quantityForVariant(vid) : 0
      if (inCart > 0) {
        const v = escapeHtml(String(vid))
        slot.innerHTML = `<div class="sai-z99nr29s__minipdp-stepper">
            <button type="button" class="sai-z99nr29s__stepper-btn" data-action="qty-dec" data-variant-id="${v}" aria-label="Decrease quantity">−</button>
            <span class="sai-z99nr29s__stepper-qty">${inCart}</span>
            <button type="button" class="sai-z99nr29s__stepper-btn" data-action="qty-inc" data-variant-id="${v}" aria-label="Increase quantity">+</button>
          </div>`
      } else {
        const available = !!variant?.available
        const capOk = !cart || cart.canAdd?.({ productHandle: product.handle }) !== false
        slot.innerHTML = `<button type="button" class="sai-z99nr29s__minipdp-cta" data-action="add-to-bundle" ${available && capOk ? '' : 'disabled'}>ADD TO CART</button>`
      }
    }

    /** Resolve the variant matching the current Mini-PDP selection. */
    _resolveMinipdpVariant(product) {
      for (const v of product.variants) {
        const o1 = this._minipdpSelection[1] ?? null
        const o2 = this._minipdpSelection[2] ?? null
        const o3 = this._minipdpSelection[3] ?? null
        if (
          (v.option1 ?? null) === o1 &&
          (v.option2 ?? null) === o2 &&
          (v.option3 ?? null) === o3
        ) {
          return v
        }
      }
      return null
    }

    /** Check whether any variant exists that has this option value, irrespective
     *  of the other axes (used to gate the swatch as OOS / unavailable). */
    _isOptionValueAvailable(product, position, value) {
      const key = `option${position}`
      return product.variants.some((v) => v[key] === value && v.available)
    }

    /** Locked-aware availability: a swatch is available when at least one
     *  variant matches BOTH the locked axes and this value at this position. */
    _isOptionValueAvailableWithLock(product, position, value, lockedOptions) {
      const key = `option${position}`
      return product.variants.some((v) => {
        if (!v.available) return false
        if (v[key] !== value) return false
        for (let pos = 1; pos <= 3; pos++) {
          const want = lockedOptions[pos]
          if (want != null && v[`option${pos}`] !== want) return false
        }
        return true
      })
    }

    // ── Bottom bar paint ──

    _paint(cartState) {
      if (!this._bar) return
      const target = this._config.quantityRequirement || cartState.tier?.quantity || 0
      const count = cartState.count
      const empty = count === 0
      const complete = !!cartState.tier?.isMet

      const offerEl = this._bar.querySelector('[data-slot="offer"]')
      const slotsEl = this._bar.querySelector('[data-slot="slots"]')
      const countEl = this._bar.querySelector('[data-slot="count"]')
      const totalEl = this._bar.querySelector('[data-slot="total"]')
      const expandedEl = this._bar.querySelector('[data-slot="expanded"]')
      const cta = this._bar.querySelector('[data-action="checkout"]')

      // Offer strip: black until the shopper starts → green once items are in;
      // flips to the savings/unlock copy when the bundle is complete.
      this._bar.classList.toggle('is-started', !empty)
      this._bar.classList.toggle('is-complete', complete)
      if (offerEl) {
        if (complete && cartState.savings > 0) {
          const amt = formatMoney(cartState.savings)
          offerEl.innerHTML = `<strong class="sai-z99nr29s__bar-offer-strong">Offer Unlocked!</strong> You are saving ${amt}`
        } else {
          offerEl.textContent = this._bar.dataset.emptyCopy || ''
        }
      }

      // Slots: filled thumbnails (one per unit) then dashed empty slots up to
      // the requirement. Cap the row; overflow collapses to "+N".
      if (slotsEl) {
        const max = this._props.thumbnailMaxCount ?? 5
        const filled = cartState.lines.slice(0, max)
        const overflow = cartState.lines.length - filled.length
        let html = filled
          .map(
            (l) =>
              `<span class="sai-z99nr29s__bar-thumb" style="background-image: url('${escapeHtml(l.image ?? '')}')"></span>`,
          )
          .join('')
        if (overflow > 0) html += `<span class="sai-z99nr29s__bar-overflow">+${overflow}</span>`
        const emptySlots = Math.max(0, Math.min(target, max) - filled.length - (overflow > 0 ? 1 : 0))
        for (let i = 0; i < emptySlots; i++) {
          html += '<span class="sai-z99nr29s__bar-slot-empty" aria-hidden="true"></span>'
        }
        slotsEl.innerHTML = html
      }

      if (countEl) countEl.textContent = `${count} / ${target} Products`

      if (totalEl) {
        const mrp =
          cartState.retailTotal > cartState.total
            ? `<span class="sai-z99nr29s__bar-total-mrp">${formatMoney(cartState.retailTotal)}</span>`
            : ''
        totalEl.innerHTML = `<span class="sai-z99nr29s__bar-total-label">Total:</span> <span class="sai-z99nr29s__bar-total-amount">${formatMoney(cartState.total)}</span> ${mrp}`
      }

      if (cta) {
        cta.disabled = !cartState.canCheckout
        cta.textContent = this._config.checkoutCtaLabel ?? 'Buy Bundle'
      }

      // Keep the expanded editable list in sync while it's open.
      if (expandedEl && !expandedEl.hidden) this._paintExpanded(cartState, expandedEl)

      // Update the page padding to match the bar height so the last grid row
      // isn't hidden under the bar.
      const barHeight = this._bar.offsetHeight
      this.style.setProperty('--sai-z99nr29s-bar-h', `${barHeight + 16}px`)

      // Repaint card "is-added" states.
      this._paintCardStates(cartState)

      // Keep the Mini-PDP footer (ADD ↔ stepper) in sync while it's open.
      if (this._minipdp?.dataset.open === 'true' && this._minipdpProductIdx >= 0) {
        const p = this._products[this._minipdpProductIdx]
        if (p) this._paintMinipdpCta(p, this._resolveMinipdpVariant(p))
      }
    }

    _paintCardStates(cartState) {
      if (!this._grid) return
      const groups = cartState.groups || []
      for (const card of this._grid.querySelectorAll('.sai-z99nr29s__card')) {
        if (card.classList.contains('is-oos')) continue
        const handle = card.dataset.productHandle
        const slot = card.querySelector('[data-slot="action"]')
        if (!slot) continue
        const productGroups = groups.filter((g) => g.productHandle === handle)
        const qty = productGroups.reduce((sum, g) => sum + g.quantity, 0)
        card.classList.toggle('is-added', qty > 0)
        if (qty > 0) {
          // Stepper targets the product's added variant (first group — the
          // one-variant-per-product common case).
          const vid = escapeHtml(String(productGroups[0].variantId))
          slot.innerHTML = `<div class="sai-z99nr29s__stepper">
              <button type="button" class="sai-z99nr29s__stepper-btn" data-action="qty-dec" data-variant-id="${vid}" aria-label="Decrease quantity">−</button>
              <span class="sai-z99nr29s__stepper-qty">${qty}</span>
              <button type="button" class="sai-z99nr29s__stepper-btn" data-action="qty-inc" data-variant-id="${vid}" aria-label="Increase quantity">+</button>
            </div>`
        } else {
          const cardIdx = escapeHtml(String(slot.dataset.cardIdx))
          slot.innerHTML = `<button type="button" class="sai-z99nr29s__card-add" data-action="open-minipdp" data-card-idx="${cardIdx}">ADD</button>`
        }
      }
    }

    /** Expand / collapse the bottom-bar selected-items list. */
    _toggleExpand() {
      const expandedEl = this._bar?.querySelector('[data-slot="expanded"]')
      const summary = this._bar?.querySelector('[data-action="toggle-expand"]')
      if (!expandedEl) return
      const open = expandedEl.hidden
      expandedEl.hidden = !open
      this._bar.classList.toggle('is-expanded', open)
      summary?.setAttribute('aria-expanded', String(open))
      if (open) {
        const cart = window.Spectrum?.z99nr29sCart
        if (cart) this._paintExpanded(cart.getState(), expandedEl)
      }
    }

    /** Per-product editable rows: thumbnail, title, unit price ×N + variant
     *  options, and a trash button (removes the variant via setQuantity 0). */
    _paintExpanded(cartState, el) {
      const groups = cartState.groups || []
      if (!groups.length) {
        el.innerHTML = `<div class="sai-z99nr29s__bar-row-empty">${escapeHtml(this._bar.dataset.emptyCopy || '')}</div>`
        return
      }
      el.innerHTML = groups
        .map((g) => {
          const opts =
            (g.options || []).map((o) => `${escapeHtml(o.name)}: ${escapeHtml(o.value)}`).join('  |  ') ||
            escapeHtml(g.variantTitle || '')
          const vid = escapeHtml(String(g.variantId))
          return `<div class="sai-z99nr29s__bar-row">
              <span class="sai-z99nr29s__bar-row-thumb" style="background-image: url('${escapeHtml(g.image ?? '')}')"></span>
              <div class="sai-z99nr29s__bar-row-info">
                <span class="sai-z99nr29s__bar-row-title">${escapeHtml(g.displayLabel ?? '')}</span>
                <span class="sai-z99nr29s__bar-row-meta">${formatMoney(g.price)} &times;${g.quantity}${opts ? `<span class="sai-z99nr29s__bar-row-opts">${opts}</span>` : ''}</span>
              </div>
              <button type="button" class="sai-z99nr29s__bar-row-remove" data-action="remove-variant" data-variant-id="${vid}" aria-label="Remove item">${TRASH_ICON}</button>
            </div>`
        })
        .join('')
    }

    // ── Event wiring ──

    _bindEvents() {
      this.addEventListener('click', (e) => {
        const target = e.target.closest('[data-action]')
        if (!target) return
        const action = target.dataset.action
        if (action === 'open-minipdp') {
          e.preventDefault()
          const cardIdx = Number.parseInt(target.dataset.cardIdx, 10)
          if (Number.isFinite(cardIdx)) this._openMinipdpForCard(cardIdx)
        } else if (action === 'close-minipdp') {
          e.preventDefault()
          this._closeMinipdp()
        } else if (action === 'pick-option') {
          e.preventDefault()
          if (target.classList.contains('is-oos')) return
          const pos = Number.parseInt(target.dataset.position, 10)
          const value = target.dataset.value
          if (Number.isFinite(pos) && value != null) {
            this._minipdpSelection[pos] = value
            const product = this._products[this._minipdpProductIdx]
            if (product) this._renderMinipdpBody(product)
          }
        } else if (action === 'add-to-bundle') {
          e.preventDefault()
          this._addCurrentMinipdpToBundle()
        } else if (action === 'qty-inc' || action === 'qty-dec') {
          e.preventDefault()
          const vid = target.dataset.variantId
          const cart = window.Spectrum?.z99nr29sCart
          if (vid == null || !cart) return
          const current = cart.quantityForVariant(vid)
          const next = action === 'qty-inc' ? current + 1 : current - 1
          // Backend is the authority on the cap; setQuantity(0) removes the line.
          cart.setQuantity(vid, next)
        } else if (action === 'remove-variant') {
          e.preventDefault()
          const vid = target.dataset.variantId
          const cart = window.Spectrum?.z99nr29sCart
          if (vid != null && cart) cart.setQuantity(vid, 0)
        } else if (action === 'toggle-expand') {
          e.preventDefault()
          this._toggleExpand()
        } else if (action === 'checkout') {
          e.preventDefault()
          this._checkout()
        }
      })

      // Stored on `this` so `disconnectedCallback` can remove it. Shopify's
      // theme editor + Studio preview can re-mount sections, which would
      // otherwise leak one listener (and a `this` closure) per remount.
      this._onKeyDown = (e) => {
        if (e.key === 'Escape' && this._minipdp?.dataset.open === 'true') {
          e.preventDefault()
          this._closeMinipdp()
        }
      }
      document.addEventListener('keydown', this._onKeyDown)
    }

    async _addCurrentMinipdpToBundle() {
      const product = this._products[this._minipdpProductIdx]
      if (!product) return
      const variant = this._resolveMinipdpVariant(product)
      if (!variant?.available) return
      // Stay open — the footer flips ADD → stepper once the add lands (via the
      // cart subscription → _paint → _paintMinipdpCta).
      await this._addVariantToBundle(product, variant)
    }

    /** Push a (product, variant) tuple into the bundle cart. Used by both
     *  the Mini-PDP "Add to Bundle" CTA AND the fully-resolved variant-card
     *  direct-add path. The cart engine talks to the backend, so the add is
     *  async; returns a promise resolving to whether it succeeded. */
    async _addVariantToBundle(product, variant) {
      const line = {
        productHandle: product.handle,
        variantId: variant.id,
        displayLabel: product.title,
        image: variant.image ?? product.image,
        price: typeof variant.price === 'string' ? Number.parseFloat(variant.price) : variant.price,
        compareAtPrice:
          variant.compareAtPrice == null
            ? null
            : typeof variant.compareAtPrice === 'string'
              ? Number.parseFloat(variant.compareAtPrice)
              : variant.compareAtPrice,
      }
      const cart = window.Spectrum?.z99nr29sCart
      if (!cart) return false
      const ok = await cart.add(line)
      this._track(`${FEATURE_SLUG}:product_added`, {
        product_handle: product.handle,
        variant_id: variant.id,
        success: ok,
      })
      return ok
    }

    /** Bind the SSR'd urgency banner to a 1s tick.
     *
     *  The banner is server-rendered with `data-deadline` (ISO string) and
     *  `data-template` (`"Ends in {time}"`). We start the interval, format
     *  remaining time as MM:SS or HH:MM:SS, and when the deadline passes
     *  we remove the banner outright — leaving an "expired" UI looks worse
     *  than no UI. Multiple wrappers on a page aren't a thing for BXAY
     *  pages, but if the snippet element is reconnected we clear the
     *  previous interval before starting again (defensive). */
    _startUrgencyTimer() {
      const banner = this.querySelector('[data-spectrum-bxay-urgency]')
      if (!banner) return
      if (this._urgencyInterval) clearInterval(this._urgencyInterval)

      const deadlineMs = Date.parse(banner.dataset.deadline ?? '')
      const template = banner.dataset.template || 'Ends in {time}'
      const slot = banner.querySelector('[data-slot="copy"]')
      if (!Number.isFinite(deadlineMs) || !slot) {
        // No usable deadline → don't even render the banner.
        banner.remove()
        return
      }

      const tick = () => {
        const remaining = deadlineMs - Date.now()
        if (remaining <= 0) {
          clearInterval(this._urgencyInterval)
          this._urgencyInterval = null
          banner.remove()
          this._track(`${FEATURE_SLUG}:urgency_timer_expired`, {})
          return
        }
        slot.innerHTML = template.replace(
          '{time}',
          `<span class="sai-z99nr29s__urgency-time">${formatDuration(remaining)}</span>`,
        )
      }
      tick()
      this._urgencyInterval = setInterval(tick, 1000)
    }

    async _checkout() {
      const cart = window.Spectrum?.z99nr29sCart
      if (!cart) return
      const state = cart.getState()
      this._track(`${FEATURE_SLUG}:checkout_clicked`, {
        count: state.count,
        tier_met: state.tier?.isMet,
        total_cents: state.total,
      })
      if (!state.tier?.isMet && this._config.partialCheckoutAllowed) {
        const ok = window.confirm(
          this._config.partialWarningCopy ||
            'You haven’t reached the bundle deal. Continue anyway?',
        )
        if (!ok) return
      }
      // The backend bundle cart holds the items with the fixed-price discount
      // already applied; checkout() redirects to the server checkout URL.
      await cart.checkout()
    }
  }

  // Test-harness shim — exposes the pure helpers so the explosion logic can
  // be exercised without mounting the custom element. Gated on a sentinel
  // that production never sets.
  if (typeof __SAI_BXAY_TEST__ !== 'undefined') {
    /* istanbul ignore next */
    globalThis.__sai_z99nr29s_api__ = {
      explodeProduct,
      findVariantForLocked,
      isFullyResolvedByBasis,
      lockedSelectionAvailable,
      escapeHtml,
      formatDuration,
    }
  }

  if (!customElements.get('sai-z99nr29s')) {
    customElements.define('sai-z99nr29s', SaiZ99nr29sElement)
  }
})()
